import { SubscriptionClient } from 'subscriptions-transport-ws';

interface TokenResponse {
  accessToken: string;
  expiresIn: number;
  tokenType: string;
}

interface StoredSubscription {
  query: string;
  variables?: Record<string, any>;
  onData: (data: any) => void;
  onError?: (error: any) => void;
  unsubscribe: () => void;
}

/**
 * Manages Dgraph JWT tokens and WebSocket subscriptions with automatic reconnection.
 */
export class DgraphTokenManager {
  private static readonly TOKEN_REFRESH_BUFFER_MS = 60 * 1000;
  private static readonly MAX_RETRY_ATTEMPTS = 5;
  private static readonly INITIAL_RETRY_DELAY_MS = 5000;
  private static readonly RATE_LIMIT_BACKOFF_MS = 10000;
  private static readonly TOKEN_CHECK_INTERVAL_MS = 300000;
  private static readonly MAX_CONSECUTIVE_FAILURES = 3;
  private static readonly REFRESH_DEBOUNCE_MS = 5000;

  private token: string | null = null;
  private expiresAt: number | null = null;
  private refreshTimeout: ReturnType<typeof setTimeout> | null = null;
  private retryTimeout: ReturnType<typeof setTimeout> | null = null;
  private tokenCheckInterval: ReturnType<typeof setInterval> | null = null;
  private retryAttempt = 0;
  private wsClient: SubscriptionClient | null = null;
  private activeSubscriptions: Map<string, StoredSubscription> = new Map();
  private isRefreshing = false;
  private rateLimitedSubscriptions: Map<
    string,
    { retryCount: number; timeout: ReturnType<typeof setTimeout> }
  > = new Map();
  private tokenRequestPromise: Promise<string> | null = null;
  private refreshPromise: Promise<void> | null = null;
  private lastRefreshTime: number = 0;
  private consecutiveFailures = 0;
  private isDestroyed = false;
  private isActive = false;

  constructor(
    private readonly dgraphWss: string,
    private readonly organisationBid: string,
    private readonly userAccessToken: string,
    private readonly dgraphNamespace: string,
    private readonly getTokenUrl: (orgBid: string) => string,
  ) {
    if (!dgraphWss || !organisationBid || !userAccessToken) {
      throw new Error('DgraphTokenManager requires all constructor parameters');
    }

    if (!dgraphNamespace) {
      console.debug(
        'DgraphTokenManager: dgraphNamespace not provided, token monitoring will not start',
      );
      this.isActive = false;
      return;
    }

    this.isActive = true;
    this.startTokenMonitoring();
  }

  private startTokenMonitoring(): void {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
    }

    this.tokenCheckInterval = setInterval(async () => {
      if (this.isDestroyed || this.isRefreshing || this.refreshPromise) return;

      try {
        if (
          this.expiresAt &&
          this.expiresAt <= Date.now() + DgraphTokenManager.TOKEN_REFRESH_BUFFER_MS
        ) {
          await this.refreshConnection();
        } else if (!this.token || !this.expiresAt) {
          await this.getToken();
        }
        this.consecutiveFailures = 0;
      } catch (error) {
        console.error('Error during token monitoring:', error);
        this.consecutiveFailures++;

        if (this.consecutiveFailures >= DgraphTokenManager.MAX_CONSECUTIVE_FAILURES) {
          console.warn(
            `Token monitoring failed ${this.consecutiveFailures} times, attempting recovery`,
          );
          try {
            await this.refreshConnection();
          } catch (recoveryError) {
            console.error('Token monitoring recovery failed:', recoveryError);
          }
        }
      }
    }, DgraphTokenManager.TOKEN_CHECK_INTERVAL_MS);
  }

  async getToken(): Promise<string> {
    if (this.isDestroyed) {
      throw new Error('DgraphTokenManager has been destroyed');
    }

    if (!this.isActive) {
      throw new Error('DgraphTokenManager is not active - no dgraph namespace provided');
    }

    const now = Date.now();

    if (this.token && this.expiresAt) {
      const timeUntilExpiry = this.expiresAt - now;
      if (timeUntilExpiry > DgraphTokenManager.TOKEN_REFRESH_BUFFER_MS) {
        return this.token;
      }
      this.token = null;
      this.expiresAt = null;
    }

    if (this.tokenRequestPromise) {
      return this.tokenRequestPromise;
    }

    this.tokenRequestPromise = this.fetchNewToken();

    try {
      return await this.tokenRequestPromise;
    } catch (error) {
      console.error('Failed to obtain Dgraph token:', error);
      throw error;
    } finally {
      this.tokenRequestPromise = null;
    }
  }

  private async fetchNewToken(): Promise<string> {
    const response = await fetch(this.getTokenUrl(this.organisationBid), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.userAccessToken}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to get Dgraph token: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    const tokenData = (await response.json()) as TokenResponse;

    this.token = tokenData.accessToken;
    this.expiresAt = Date.now() + tokenData.expiresIn * 1000;

    const expiresInMinutes = Math.round(tokenData.expiresIn / 60);
    console.log(`Dgraph token obtained: expires in ${expiresInMinutes} minutes`);

    if (!this.isRefreshing) {
      this.scheduleRefresh();
    }

    return tokenData.accessToken;
  }

  private scheduleRefresh() {
    if (this.refreshTimeout) clearTimeout(this.refreshTimeout);
    if (this.retryTimeout) clearTimeout(this.retryTimeout);

    if (!this.expiresAt) {
      this.scheduleRetry();
      return;
    }

    const refreshTime = this.expiresAt - Date.now() - DgraphTokenManager.TOKEN_REFRESH_BUFFER_MS;

    if (refreshTime <= 0) {
      this.scheduleRetry();
      return;
    }

    this.refreshTimeout = setTimeout(async () => {
      try {
        await this.refreshConnection();
        this.retryAttempt = 0;
      } catch (error) {
        console.warn('Token refresh failed, scheduling retry:', error);
        this.scheduleRetry();
      }
    }, refreshTime);
  }

  private scheduleRetry() {
    if (this.retryTimeout) clearTimeout(this.retryTimeout);

    if (this.retryAttempt >= DgraphTokenManager.MAX_RETRY_ATTEMPTS) {
      console.error('Max retry attempts reached for token refresh');
      return;
    }

    const delay = DgraphTokenManager.INITIAL_RETRY_DELAY_MS * Math.pow(2, this.retryAttempt);

    this.retryTimeout = setTimeout(async () => {
      if (this.isDestroyed) return;

      this.retryAttempt++;

      try {
        await this.refreshConnection();
        this.retryAttempt = 0;
      } catch (error) {
        console.warn(`Token refresh retry ${this.retryAttempt} failed:`, error);

        if (this.retryAttempt < DgraphTokenManager.MAX_RETRY_ATTEMPTS) {
          this.scheduleRetry();
        } else {
          console.error('All token refresh retry attempts exhausted');
        }
      }
    }, delay);
  }

  private async refreshConnection(): Promise<void> {
    if (this.isDestroyed) {
      throw new Error('DgraphTokenManager has been destroyed');
    }

    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const now = Date.now();
    if (now - this.lastRefreshTime < DgraphTokenManager.REFRESH_DEBOUNCE_MS) {
      return;
    }

    if (this.isRefreshing) return;

    this.refreshPromise = this.performRefresh();
    return this.refreshPromise;
  }

  private async performRefresh(): Promise<void> {
    this.isRefreshing = true;
    this.lastRefreshTime = Date.now();

    try {
      const subscriptionsToRestore = Array.from(this.activeSubscriptions.entries());

      if (this.wsClient) {
        this.wsClient.close(false, false);
        this.wsClient = null;
      }

      this.token = null;
      this.expiresAt = null;
      if (this.refreshTimeout) {
        clearTimeout(this.refreshTimeout);
        this.refreshTimeout = null;
      }

      Array.from(this.rateLimitedSubscriptions.values()).forEach(rateLimitRetry => {
        if (rateLimitRetry.timeout) {
          clearTimeout(rateLimitRetry.timeout);
        }
      });
      this.rateLimitedSubscriptions.clear();

      await this.getToken();

      if (!this.token || !this.isRefreshing) {
        throw new Error('Token refresh failed or refresh was cancelled');
      }

      await this.createWebSocketClient();

      if (!this.wsClient) {
        throw new Error('Failed to create WebSocket client');
      }

      this.activeSubscriptions.clear();

      let restoredCount = 0;
      let failedCount = 0;
      const maxFailures = Math.min(subscriptionsToRestore.length, 5);

      for (const [subscriptionId, subscription] of subscriptionsToRestore) {
        if (failedCount >= maxFailures || this.isDestroyed) break;

        try {
          const originalConsecutiveFailures = this.consecutiveFailures;

          await this.subscribe(
            subscriptionId,
            subscription.query,
            subscription.onData,
            (error: any) => {
              this.consecutiveFailures = originalConsecutiveFailures;
              subscription.onError?.(error);
            },
            subscription.variables,
          );

          restoredCount++;
        } catch (error) {
          failedCount++;
          console.warn(`Failed to restore subscription ${subscriptionId}:`, error);
        }
      }

      console.log(
        `Subscription restoration completed: ${restoredCount} restored, ${failedCount} failed`,
      );
      this.consecutiveFailures = 0;
    } catch (error) {
      if (this.wsClient) {
        this.wsClient.close(false, false);
        this.wsClient = null;
      }
      throw error;
    } finally {
      this.isRefreshing = false;
      setTimeout(() => {
        this.refreshPromise = null;
      }, DgraphTokenManager.REFRESH_DEBOUNCE_MS);
    }
  }

  async createWebSocketClient(): Promise<SubscriptionClient> {
    if (this.isDestroyed) {
      throw new Error('DgraphTokenManager has been destroyed');
    }

    if (!this.isActive) {
      throw new Error('DgraphTokenManager is not active - no dgraph namespace provided');
    }

    const getConnectionParams = async () => {
      if (this.isDestroyed) {
        if (this.token) {
          return { 'X-Dgraph-AccessToken': this.token };
        }
        throw new Error(
          'Cannot get connection params - DgraphTokenManager instance has been destroyed',
        );
      }

      if (
        this.token &&
        this.expiresAt &&
        this.expiresAt > Date.now() + DgraphTokenManager.TOKEN_REFRESH_BUFFER_MS
      ) {
        return { 'X-Dgraph-AccessToken': this.token };
      }

      const token = await this.getToken();
      if (!token) {
        throw new Error('No Dgraph access token available for WebSocket connection');
      }
      return { 'X-Dgraph-AccessToken': token };
    };

    this.wsClient = new SubscriptionClient(this.dgraphWss, {
      connectionParams: getConnectionParams,
      reconnect: true,
      reconnectionAttempts: 10,
      timeout: 10000,
      lazy: false,
      connectionCallback: error => {
        if (error) {
          console.warn('WebSocket connection callback error:', error);
        }
      },
    });

    this.wsClient.onError((error: Error | Event) => {
      if (this.isDestroyed || this.refreshPromise || this.isRefreshing) return;

      const errorMessage = error instanceof Error ? error.message : error.toString();

      if (
        errorMessage.includes('closed before the connection is established') ||
        errorMessage.includes('connecting') ||
        errorMessage.includes('opening')
      ) {
        return;
      }

      const isTokenExpiredError =
        /token is expired|token has invalid claims|jwt|unauthenticated|unable to parse jwt token/i.test(
          errorMessage,
        );
      const isGeneralAuthError =
        /auth|token|unauthorized|403|401|interrupted|network|disconnect/i.test(errorMessage);

      if (isTokenExpiredError || isGeneralAuthError) {
        console.warn('WebSocket error detected, triggering refresh:', errorMessage);

        const refreshDelay = isTokenExpiredError ? 0 : 2000;

        setTimeout(() => {
          if (!this.isRefreshing && !this.refreshPromise && !this.isDestroyed) {
            this.refreshConnection().catch(refreshError => {
              console.warn('Connection refresh failed:', refreshError);
            });
          }
        }, refreshDelay);
      }
    });

    return this.wsClient;
  }

  private handleSubscriptionError(
    subscriptionId: string,
    error: any,
    onError?: (error: any) => void,
  ) {
    this.consecutiveFailures++;

    const isRateLimitError =
      error?.message?.includes('429') ||
      error?.message?.toLowerCase().includes('too many requests');

    const isTokenExpiredError =
      error?.message?.toLowerCase().includes('token is expired') ||
      error?.message?.toLowerCase().includes('token has invalid claims') ||
      error?.message?.toLowerCase().includes('jwt') ||
      error?.message?.toLowerCase().includes('unauthenticated') ||
      error?.message?.toLowerCase().includes('unable to parse jwt token');

    if (isTokenExpiredError) {
      console.warn(
        `Subscription ${subscriptionId} failed due to token expiration, triggering refresh`,
      );

      if (!this.isRefreshing && !this.refreshPromise) {
        this.refreshConnection().catch(refreshError => {
          console.error('Token refresh failed after expiration:', refreshError);
          if (!this.isDestroyed) {
            onError?.(error);
          }
        });
      } else {
        if (this.refreshPromise) {
          this.refreshPromise.catch(refreshError => {
            console.error('Ongoing token refresh failed after expiration:', refreshError);
            if (!this.isDestroyed) {
              onError?.(error);
            }
          });
        } else {
          setTimeout(() => {
            if (!this.isDestroyed && this.consecutiveFailures > 0) {
              onError?.(error);
            }
          }, 5000);
        }
      }
      return;
    }

    const currentFailures = this.consecutiveFailures;
    if (
      currentFailures >= DgraphTokenManager.MAX_CONSECUTIVE_FAILURES &&
      !this.isRefreshing &&
      !this.refreshPromise
    ) {
      console.warn(
        `${currentFailures} consecutive subscription failures detected, forcing token refresh`,
      );

      this.refreshConnection().catch(refreshError => {
        console.error('Forced token refresh failed:', refreshError);
      });
    }

    if (isRateLimitError) {
      const existingRetry = this.rateLimitedSubscriptions.get(subscriptionId);
      const retryCount = (existingRetry?.retryCount ?? 0) + 1;

      if (existingRetry?.timeout) {
        clearTimeout(existingRetry.timeout);
      }

      if (retryCount > 5) {
        console.error(`Subscription ${subscriptionId} exceeded max rate limit retries, giving up`);

        this.rateLimitedSubscriptions.delete(subscriptionId);

        const subscription = this.activeSubscriptions.get(subscriptionId);
        if (subscription?.unsubscribe) {
          try {
            subscription.unsubscribe();
          } catch (unsubError) {
            console.warn(`Error unsubscribing failed subscription ${subscriptionId}:`, unsubError);
          }
        }
        this.activeSubscriptions.delete(subscriptionId);

        onError?.(new Error(`Rate limit retry limit exceeded for subscription ${subscriptionId}`));
        return;
      }

      const baseDelay = DgraphTokenManager.RATE_LIMIT_BACKOFF_MS * Math.pow(2, retryCount - 1);
      const jitter = Math.random() * 1000;
      const backoffDelay = baseDelay + jitter;

      console.warn(
        `Subscription ${subscriptionId} rate limited. Retrying in ${(backoffDelay / 1000).toFixed(1)}s (attempt ${retryCount})`,
      );

      const timeout = setTimeout(async () => {
        if (this.isDestroyed) {
          this.rateLimitedSubscriptions.delete(subscriptionId);
          return;
        }

        try {
          const subscription = this.activeSubscriptions.get(subscriptionId);
          if (subscription) {
            subscription.unsubscribe();

            await this.subscribe(
              subscriptionId,
              subscription.query,
              subscription.onData,
              subscription.onError,
              subscription.variables,
            );

            this.rateLimitedSubscriptions.delete(subscriptionId);
          } else {
            this.rateLimitedSubscriptions.delete(subscriptionId);
          }
        } catch (retryError) {
          console.error(`Failed to retry rate-limited subscription ${subscriptionId}:`, retryError);
          this.rateLimitedSubscriptions.delete(subscriptionId);
          this.activeSubscriptions.delete(subscriptionId);

          if (!this.isDestroyed) {
            onError?.(retryError);
          }
        }
      }, backoffDelay);

      this.rateLimitedSubscriptions.set(subscriptionId, {
        retryCount,
        timeout,
      });
    } else {
      this.activeSubscriptions.delete(subscriptionId);
      onError?.(error);
    }
  }

  async subscribe(
    subscriptionId: string,
    query: string,
    onData: (data: any) => void,
    onError?: (error: any) => void,
    variables?: Record<string, any>,
  ): Promise<() => void> {
    if (this.isDestroyed) {
      throw new Error('DgraphTokenManager has been destroyed');
    }

    if (!this.isActive) {
      throw new Error('DgraphTokenManager is not active - no dgraph namespace provided');
    }

    if (!subscriptionId || typeof subscriptionId !== 'string') {
      throw new Error('subscriptionId must be a non-empty string');
    }
    if (!query || typeof query !== 'string') {
      throw new Error('query must be a non-empty string');
    }
    if (!onData || typeof onData !== 'function') {
      throw new Error('onData callback is required and must be a function');
    }
    if (onError && typeof onError !== 'function') {
      throw new Error('onError must be a function if provided');
    }

    if (this.activeSubscriptions.has(subscriptionId)) {
      console.warn(`Subscription ${subscriptionId} already exists, unsubscribing previous one`);
      await this.unsubscribe(subscriptionId);
    }

    if (!this.wsClient) {
      await this.createWebSocketClient();
    }

    if (!this.wsClient) {
      throw new Error('Failed to create WebSocket client for subscription');
    }

    try {
      const observable = this.wsClient.request({
        query,
        variables,
      });

      const subscription = observable.subscribe({
        next: data => {
          if (this.isDestroyed) return;
          this.consecutiveFailures = 0;
          try {
            onData(data);
          } catch (error) {
            console.error(`Error in onData callback for subscription ${subscriptionId}:`, error);
          }
        },
        error: error => {
          if (this.isDestroyed) return;
          this.handleSubscriptionError(subscriptionId, error, onError);
        },
        complete: () => {
          this.activeSubscriptions.delete(subscriptionId);
          this.rateLimitedSubscriptions.delete(subscriptionId);
        },
      });

      const unsubscribe = () => {
        try {
          subscription.unsubscribe();
        } catch (error) {
          console.warn(`Error unsubscribing from ${subscriptionId}:`, error);
        }

        const rateLimitRetry = this.rateLimitedSubscriptions.get(subscriptionId);
        if (rateLimitRetry?.timeout) {
          clearTimeout(rateLimitRetry.timeout);
        }
        this.rateLimitedSubscriptions.delete(subscriptionId);
        this.activeSubscriptions.delete(subscriptionId);
      };

      this.activeSubscriptions.set(subscriptionId, {
        query,
        variables,
        onData,
        onError,
        unsubscribe,
      });

      return unsubscribe;
    } catch (error) {
      console.error(`Failed to create subscription ${subscriptionId}:`, error);
      throw error;
    }
  }

  async unsubscribe(subscriptionId: string): Promise<void> {
    if (!subscriptionId || typeof subscriptionId !== 'string') {
      throw new Error('subscriptionId must be a non-empty string');
    }

    const storedSubscription = this.activeSubscriptions.get(subscriptionId);
    if (storedSubscription?.unsubscribe) {
      try {
        storedSubscription.unsubscribe();
      } catch (error) {
        console.warn(`Error unsubscribing from ${subscriptionId}:`, error);
      }
    }

    this.activeSubscriptions.delete(subscriptionId);

    const rateLimitRetry = this.rateLimitedSubscriptions.get(subscriptionId);
    if (rateLimitRetry?.timeout) {
      clearTimeout(rateLimitRetry.timeout);
    }
    this.rateLimitedSubscriptions.delete(subscriptionId);
  }

  getConnectionInfo() {
    const wsReadyState = this.wsClient?.client?.readyState;
    const wsReadyStateText =
      wsReadyState !== undefined && wsReadyState !== null
        ? ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][wsReadyState] || 'UNKNOWN'
        : 'NO_CLIENT';

    return {
      activeSubscriptions: this.activeSubscriptions.size,
      subscriptionIds: Array.from(this.activeSubscriptions.keys()),
      hasToken: !!this.token,
      tokenExpiresAt: this.expiresAt,
      tokenExpiresIn: this.expiresAt ? Math.max(0, this.expiresAt - Date.now()) : null,
      isConnected: !!this.wsClient,
      isRefreshing: this.isRefreshing,
      pendingRefresh: !!this.refreshPromise,
      lastRefreshTime: this.lastRefreshTime,
      timeSinceLastRefresh: Date.now() - this.lastRefreshTime,
      wsClient: this.wsClient?.client
        ? {
            readyState: wsReadyState,
            readyStateText: wsReadyStateText,
            url: this.wsClient.client.url || null,
            protocol: this.wsClient.client.protocol || null,
            extensions: this.wsClient.client.extensions || null,
          }
        : null,
      rateLimitedSubscriptions: this.rateLimitedSubscriptions.size,
      retryAttempt: this.retryAttempt,
      hasTokenRequestInProgress: !!this.tokenRequestPromise,
      tokenCheckInterval: !!this.tokenCheckInterval,
      consecutiveFailures: this.consecutiveFailures,
      isDestroyed: this.isDestroyed,
      isActive: this.isActive,
      isHealthy: this.isHealthy(),
    };
  }

  isHealthy(): boolean {
    if (this.isDestroyed) return false;

    // If not active (no dgraph namespace), consider it "healthy" in the sense that it's not broken
    if (!this.isActive) return true;

    const tokenExpiryThreshold = Date.now() + DgraphTokenManager.TOKEN_REFRESH_BUFFER_MS;
    const hasValidToken = !!(this.token && this.expiresAt && this.expiresAt > tokenExpiryThreshold);

    const wsReadyState = this.wsClient?.client?.readyState;
    const wsConnected = wsReadyState === 1;

    const notStuckRefreshing = !this.isRefreshing || Date.now() - this.lastRefreshTime < 60000;

    const failuresAcceptable =
      this.consecutiveFailures < DgraphTokenManager.MAX_CONSECUTIVE_FAILURES;

    return hasValidToken && wsConnected && notStuckRefreshing && failuresAcceptable;
  }

  async manualRefresh(): Promise<void> {
    if (this.isDestroyed) {
      throw new Error('DgraphTokenManager has been destroyed');
    }

    if (!this.isActive) {
      throw new Error('DgraphTokenManager is not active - no dgraph namespace provided');
    }

    if (this.refreshPromise) {
      await this.refreshPromise;
      return;
    }

    const timeSinceLastRefresh = Date.now() - this.lastRefreshTime;
    if (timeSinceLastRefresh < DgraphTokenManager.REFRESH_DEBOUNCE_MS) {
      return;
    }

    const now = Date.now();
    const tokenNeedsRefresh =
      !this.token ||
      !this.expiresAt ||
      this.expiresAt <= now + DgraphTokenManager.TOKEN_REFRESH_BUFFER_MS;
    const connectionUnhealthy = !this.isHealthy();

    if (!tokenNeedsRefresh && !connectionUnhealthy) {
      return;
    }

    this.consecutiveFailures = 0;

    return this.refreshConnection();
  }

  cleanup(): void {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }

    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }

    Array.from(this.rateLimitedSubscriptions.values()).forEach(rateLimitRetry => {
      if (rateLimitRetry.timeout) {
        clearTimeout(rateLimitRetry.timeout);
      }
    });
    this.rateLimitedSubscriptions.clear();

    this.retryAttempt = 0;

    const subscriptionsArray = Array.from(this.activeSubscriptions.entries());
    for (const [subscriptionId, subscription] of subscriptionsArray) {
      try {
        subscription.unsubscribe();
      } catch (error) {
        console.warn(`Failed to unsubscribe from ${subscriptionId}:`, error);
      }
    }

    this.activeSubscriptions.clear();

    if (this.wsClient) {
      try {
        this.wsClient.close(false, false);
      } catch (error) {
        console.warn('Error closing WebSocket client:', error);
      }
      this.wsClient = null;
    }

    this.token = null;
    this.expiresAt = null;
    this.isRefreshing = false;
    this.tokenRequestPromise = null;
    this.refreshPromise = null;
    this.lastRefreshTime = 0;
    this.consecutiveFailures = 0;

    setTimeout(() => {
      this.isDestroyed = true;
    }, 200);
  }
}
